/**
 * The Explore map's viewport endpoint (spec §1.2, §1.5, §1.8).
 *
 * One request answers both surfaces: the pins the map draws and the cards the
 * list shows come from the same response, because §1.5 makes the list "the same
 * set as the pins in view, as cards". Two calls could disagree, and a list that
 * disagreed with the map beside it is the failure the shared endpoint prevents.
 */
import { fetchApi } from './apiBase';

/** A pin NEVER carries a count of zero (§1.2). The key is absent on a guide
 *  pin rather than null or zero, so a client that renders `count` when present
 *  cannot accidentally print a 0 — which is why this is optional, not
 *  `number | null`. */
export interface ExplorePin {
  slug: string;
  name: string;
  lat: number;
  lng: number;
  state: 'numbered' | 'guide';
  count?: number;
}

/** A cluster counts PLACES; a pin's number counts PLANS. They are separate
 *  keys in the response for that reason and must stay separate here: rendering
 *  them alike would make one number mean two things on one screen. */
export interface ExploreCluster {
  places: number;
  lat: number;
  lng: number;
}

export interface ExplorePhoto {
  url: string;
  thumb_url: string;
  photographer: string;
  unsplash_link: string;
  download_location: string;
}

export interface ExploreCard {
  slug: string;
  name: string;
  lat: number | null;
  lng: number | null;
  position: number;
  /** Complete-with-credit or absent. There is no shape here that lets a caller
   *  render an image without the photographer beside it, and that is
   *  deliberate: a consolidated page credit does not satisfy Unsplash's terms. */
  photo: ExplorePhoto | null;
  hook: string;
  person: { name: string } | null;
  /** Real uses: public, live copies; 0 renders nothing. */
  used_count: number;
  guide: { edition_number: number; title: string } | null;
  reason: { kind: string; text: string } | null;
  reasons: [string, string][];
  /** The trip's own shape, which the row leads with (Sep-7 field round,
   *  issue 3). `days` not nights: a plan IS a day-by-day
   *  structure, and the length filter buckets on the same number, so a row
   *  counting nights would disagree with the chip that selects it. Words are
   *  server-owned — an unrecognised season or group arrives as '' rather than
   *  as a raw database token. */
  trip: { days: number | null; band: string; season: string; group: string } | null;
}

/** The row's own description: "5 days · summer · couples".
 *
 *  Server-owned vocabulary, joined here. The parts arrive already decided —
 *  an unrecognised season or group comes back as '' rather than as a raw
 *  column value — so this only pluralises the day count and drops the empties.
 *  A card with nothing to say gets no line at all rather than a row of
 *  separators. */
export const tripLine = (trip: ExploreCard['trip']): string => {
  if (!trip) return '';
  const parts = [
    trip.days ? `${trip.days} ${trip.days === 1 ? 'day' : 'days'}` : '',
    // The glyph arrives ready — `$$`, never the stored word, and never for a
    // plan nobody has judged. It sits SECOND, beside the length, because both
    // are facts about the shape of the trip; season and audience are about its
    // character. Note it is a BAND, relative to the destination: a `$` week in
    // Tokyo still costs more than `$$$` in rural Portugal, so nothing here may
    // be worded as a spending cap.
    trip.band,
    trip.season,
    trip.group,
  ].filter(Boolean);
  return parts.join(' \u00b7 ');
};

export interface ExploreResponse {
  pins: ExplorePin[];
  clusters: ExploreCluster[];
  cards: ExploreCard[];
  next_cursor: string | null;
  /** Where "farther afield" begins. Carried on every page, so a client joining
   *  mid-scroll can still place the divider. */
  in_view_count: number;
}

export interface Viewport {
  west: number;
  south: number;
  east: number;
  north: number;
  zoom: number;
}

export interface ExploreFilters {
  length?: string;
  group?: string;
  /** A stored band word (`economical|mid|luxury`), never a glyph — the server
   *  refuses `?band=$$` by design. */
  band?: string;
}

/** Where the map should open (§1.3).
 *
 *  `lat`/`lng` are null when the atlas has no coordinates at all — there is no
 *  densest region of nothing, and the server says so rather than inventing a
 *  centre that would open every map at the same arbitrary spot. */
export interface ExploreFrame {
  lat: number | null;
  lng: number | null;
  radius_km: number;
  source: 'densest' | 'home' | 'unseeded';
  /** The frame was not built from a home, which is when §1.3 asks for the
   *  "Set your home" invitation. A fact about the response, not an instruction
   *  about rendering. */
  home_prompt: boolean;
}

export function fetchExploreFrame(): Promise<ExploreFrame> {
  return fetchApi<ExploreFrame>('/api/plans/explore/frame/');
}

/** The address a tap-through lands on, with §1.8's filters carried along.
 *
 *  A function rather than three lines inside a click handler, because it is the
 *  half of "the filters travel" that nothing could otherwise test: every test of
 *  the destination page STARTS at an already-filtered URL, so dropping the query
 *  here — sending every reader to the unfiltered page — passed all of them.
 *
 *  `any` is dropped rather than sent. The server reads it as the absence of a
 *  filter, so carrying it would put a filter in the address that is not one,
 *  and the page would wear the chip while showing everything. */
export function destinationHref(
  slug: string, filters: ExploreFilters = {}, opts: { jumpToSolePlan?: boolean } = {},
): string {
  const params = new URLSearchParams();
  if (filters.length && filters.length !== 'any') params.set('length', filters.length);
  if (filters.group && filters.group !== 'any') params.set('group', filters.group);
  if (filters.band && filters.band !== 'any') params.set('band', filters.band);
  // SLICE D. `jump=plan` says the reader did not ask for the destination page:
  // they pressed a place on Explore, and a place holding exactly one plan
  // should hand them the plan rather than a page whose content above the fold
  // is a control reading "1 plan for Chatham". OPT-IN, because the same
  // address is reached by people who do want the guide — a shared link, a
  // saved guide, the Plan tab's lens. The page owns the rule; this only says
  // where the reader came from.
  if (opts.jumpToSolePlan) params.set('jump', 'plan');
  const query = params.toString();
  return `/d/${slug}${query ? `?${query}` : ''}`;
}

/** One card of the reader's own list — the answer for a tapped pin whose card
 *  sits past the pages they have loaded (sheet layout decision 6, slice C).
 *
 *  `in_view_count` travels with it for the same reason it travels on every
 *  page: `position` alone does not say which side of the divider a row is on.
 *
 *  NO `zoom`. It decides how the map draws pins and takes no part in the
 *  snapshot's fingerprint, so sending it would put a value in the request that
 *  changes nothing — and one that disagreed with the list's would read as
 *  though it did. The cursor is required, not optional: this answers about the
 *  order the reader is holding, and a server minting a fresh one would answer
 *  about a list they are not looking at. */
export interface ExploreCardResult {
  card: ExploreCard;
  in_view_count: number;
}

export function fetchExploreCard(
  viewport: Viewport,
  filters: ExploreFilters,
  cursor: string,
  slug: string,
): Promise<ExploreCardResult> {
  const params = new URLSearchParams({
    bbox: `${viewport.west},${viewport.south},${viewport.east},${viewport.north}`,
    slug,
    cursor,
  });
  if (filters.length) params.set('length', filters.length);
  if (filters.group) params.set('group', filters.group);
  if (filters.band) params.set('band', filters.band);
  return fetchApi<ExploreCardResult>(
    `/api/plans/explore/card/?${params.toString()}`);
}

/** A cursor is refused by any viewport or filters other than the ones that
 *  minted it, so the caller must send back exactly what it asked with. */
export function fetchExplore(
  viewport: Viewport,
  filters: ExploreFilters = {},
  cursor?: string,
): Promise<ExploreResponse> {
  const params = new URLSearchParams({
    bbox: `${viewport.west},${viewport.south},${viewport.east},${viewport.north}`,
    zoom: String(viewport.zoom),
  });
  if (filters.length) params.set('length', filters.length);
  if (filters.group) params.set('group', filters.group);
  if (filters.band) params.set('band', filters.band);
  if (cursor) params.set('cursor', cursor);
  return fetchApi<ExploreResponse>(`/api/plans/explore/?${params.toString()}`);
}
